import { MAX_ARCHIVE_ENTRIES } from './constants'
import { ArchiveError } from './errors'

export class ByteReader {
  private position = 0

  constructor(private readonly bytes: Uint8Array) {}

  get offset(): number {
    return this.position
  }

  set offset(value: number) {
    this.position = value
  }

  get remaining(): number {
    return Math.max(0, this.bytes.length - this.position)
  }

  private need(length: number) {
    if (this.position + length > this.bytes.length) throw new ArchiveError('unsupported')
  }

  byte(): number {
    this.need(1)
    return this.bytes[this.position++]
  }

  take(length: number): Uint8Array {
    this.need(length)
    const slice = this.bytes.subarray(this.position, this.position + length)
    this.position += length
    return slice
  }

  uint32(): number {
    this.need(4)
    const start = this.position
    this.position += 4
    const bytes = this.bytes
    return (bytes[start] | (bytes[start + 1] << 8) | (bytes[start + 2] << 16) | (bytes[start + 3] << 24)) >>> 0
  }

  number(): bigint {
    this.need(1)
    const first = this.bytes[this.position++]
    let mask = 0x80
    let value = 0n
    for (let i = 0; i < 8; i++) {
      if ((first & mask) === 0) return value + BigInt(first & (mask - 1)) * 256n ** BigInt(i)
      this.need(1)
      value += BigInt(this.bytes[this.position++]) * 256n ** BigInt(i)
      mask >>= 1
    }
    return value
  }

  count(bytesPerItem = 0): number {
    return checkCount(toSafeNumber(this.number()), this.remaining, bytesPerItem)
  }

  bits(count: number): boolean[] {
    checkCount(count, this.remaining, 1 / 8)
    const out: boolean[] = []
    let current = 0
    let mask = 0
    for (let i = 0; i < count; i++) {
      if (!mask) {
        current = this.byte()
        mask = 0x80
      }
      out.push(Boolean(current & mask))
      mask >>= 1
    }
    return out
  }

  boolVector(count: number, bytesPerDefined = 0): boolean[] {
    checkCount(count, this.remaining)
    if (!this.byte()) return this.bits(count)
    checkCount(count, this.remaining, bytesPerDefined)
    return new Array<boolean>(count).fill(true)
  }
}

export const checkCount = (count: number, remaining: number, bytesPerItem = 0): number => {
  if (!Number.isSafeInteger(count) || count < 0 || count > MAX_ARCHIVE_ENTRIES)
    throw new ArchiveError('header-too-large')
  if (bytesPerItem > 0 && count * bytesPerItem > remaining) throw new ArchiveError('unsupported')
  return count
}

export const toSafeNumber = (value: bigint): number => {
  if (value < 0n || value > BigInt(Number.MAX_SAFE_INTEGER)) throw new ArchiveError('out-of-range')
  return Number(value)
}

export const readUint64 = (view: DataView, offset: number): bigint => view.getBigUint64(offset, true)

export async function readRange(file: Blob, start: number, end: number, cap: number): Promise<Uint8Array> {
  if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || end < start) throw new ArchiveError('out-of-range')
  if (end - start > cap) throw new ArchiveError('header-too-large')
  if (end > file.size) throw new ArchiveError('out-of-range')
  const bytes = new Uint8Array(await file.slice(start, end).arrayBuffer())
  if (bytes.length !== end - start) throw new ArchiveError('out-of-range')
  return bytes
}
